/* eslint-disable sonarjs/slow-regex */
import { FileUploader, Gallery } from 'devextreme-react'
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react'
import Tesseract from 'tesseract.js'
import { FileUploaderRef, FileUploaderTypes } from 'devextreme-react/cjs/file-uploader'
import LoadingPanel from '@/components/loading-panel/LoadingPanel'
import { useScreenSize } from '@/themes/media-query'
import { spesa } from '@/model/qsadminapi/QsAdminApiModuleModel'
import { useQsAdminApiManager } from '@/auth/api/qsadminapiManager'

export type IReceiptOCRRef = {
  clear: () => void
}
interface ReceiptOCRProps {
  expense?: spesa
  showImagePreview?: boolean
  showExtractedText?: boolean
  onChanged?: (data: Date | null, totale: number | null, file: File | null) => void
  creating?: boolean
}
interface Image {
  src: string
  title: string
  link: string
  alt: string
}

// eslint-disable-next-line react/display-name
const ReceiptOCR = forwardRef<IReceiptOCRRef, ReceiptOCRProps>(
  (props: ReceiptOCRProps, ref: React.ForwardedRef<IReceiptOCRRef>) => {
    const refFileUploader = useRef<FileUploaderRef>(null)
    const [loading, setLoading] = useState<boolean>(false)
    const [imageGallery, setImageGallery] = useState<Image[]>([])
    const [imageUpload, setImageUpload] = useState<Image | null>(null)
    const currentScreenSize = useScreenSize()
    const [isLoading, setIsLoading] = useState(!props.creating)
    const [perm, setPerm] = useState<boolean>(true)
    const { client } = useQsAdminApiManager()

    //metodi esposti dal componente
    useImperativeHandle(ref, () => {
      return {
        clear() {
          if (refFileUploader.current?.instance().option('value')?.length !== 0) {
            refFileUploader.current?.instance().clear()
            setImageUpload(null)
            setPerm(true)
            setIsLoading(true)
          }
        },
      } satisfies IReceiptOCRRef
    }, [])

    const resize = (file: File, size = 800, quality = 1) => {
      return new Promise((resolve, reject) => {
        const reader = new FileReader()

        reader.onload = () => {
          const img = new Image()
          img.onload = () => {
            const scale = Math.min(size / img.width, size / img.height)
            const canvas = document.createElement('canvas')
            canvas.width = img.width * scale
            canvas.height = img.height * scale
            console.log('canvas', canvas)
            const ctx = canvas.getContext('2d')
            ctx?.drawImage(img, 0, 0, canvas.width, canvas.height)
            // resolve(canvas.toDataURL()) // base64 image ready for Tesseract
            canvas.toBlob(
              (compressedBlob) => {
                if (compressedBlob) {
                  resolve(compressedBlob) // blob image ready for Tesseract
                } else {
                  reject(new Error('Compression failed'))
                }
              },
              'image/jpeg', // or 'image/png'
              quality, // 0 to 1
            )
          }
          img.onerror = reject
          img.src = reader.result as string
        }

        reader.onerror = reject
        reader.readAsDataURL(file)
      })
    }

    const onValueChanged = async (e: FileUploaderTypes.ValueChangedEvent) => {
      const file = e.value?.[0]
      if (!file) return
      console.log('file', file)
      let finalToUpload: File = file
      if (file.size / 1024 > 1024) {
        finalToUpload = (await resize(file)) as File
      }
      const reader = new FileReader()
      reader.onload = async () => {
        extractTextToImage(reader.result as string, finalToUpload)
      }
      reader.readAsDataURL(finalToUpload as Blob)
    }

    const extractTextToImage = (imageBase64: string, file: File) => {
      if (props.creating) {
        if (!imageBase64) {
          return
        }
        setLoading(true)
        Tesseract.recognize(imageBase64, 'ita+eng', {
          // logger: (m) => console.log(m), // optional: logs progress
        })
          .then(({ data: { text } }) => {
            const normalizedText = text
              .toLowerCase()
              .replaceAll(/\s+/g, ' ')
              .replaceAll(',', '.')
              .replaceAll('-', '/')
              .replaceAll('*', '')
            if (props.onChanged) props.onChanged(extractDate(normalizedText), extractTotalAmount(normalizedText), file)
          })
          .catch((err) => {
            console.error(err)
            if (props.onChanged) props.onChanged(null, null, file)
          })
          .finally(() => {
            setLoading(false)
          })
      } else {
        if (props.onChanged) props.onChanged(null, null, file)
      }
      setImageUpload({ src: imageBase64, title: file.name, alt: file.name, link: '' })
    }

    const extractTotalAmount = (text: string) => {
      const patterns = [
        /importo[^€\d]*€?\s*(\d+[.,]\d{2})/,
        /totale\s*complessivo[^€\d]*€?\s*(\d+[.,]\d{2})/,
        /totale\s*euro[^€\d]*€?\s*(\d+[.,]\d{2})/,
        /totale[^€\d]*€?\s*(\d+[.,]\d{2})/, // e.g. "Totale €12,34" or "Totale 12,34"
        /importo\s*totale[^€\d]*€?\s*(\d+[.,]\d{2})/, // e.g. "Importo totale €12,34"
        /totale\s*da\s*pagare[^€\d]*€?\s*(\d+[.,]\d{2})/, // e.g. "Totale da pagare €12,34"
        /€\s*(\d+[.,]\d{2})/, // fallback: any euro amount
      ]

      for (const pattern of patterns) {
        const match = text.match(pattern)
        if (match) return Number(match[1])
      }
      return null
    }

    const extractDate = (text: string) => {
      const patterns = [
        /\b((?:\d{2}[/-]){2}\d{4})\b/, // e.g. 12/05/2023 or 12-05-2023
        /\b(\d{4}(?:[/-]\d{2}){2})\b/, // e.g. 2023-05-12
        /\b((?:\d{2}[/-]){2}\d{2})\b/, // e.g. 12/05/23
        /\b(\d{1,2}\s+\w+\s+\d{4})\b/i, // e.g. 12 May 2023
        /\b(?:\d{2}\.){2}\d{4}\b/, // e.g. 22.05.2025
        /\b(?:\d{2}\.){2}\d{2}\b/, // e.g. 22.05.25
      ]

      for (const pattern of patterns) {
        const match = text.match(pattern)
        if (match) {
          const values = match[1].split('/')
          const year: number = values[2].length === 2 ? 2000 + Number(values[2]) : Number(values[2])
          const month: number = Number(values[1])
          const day: number = Number(values[0])
          return new Date(year, month - 1, day)
        }
      }
      return null
    }

    useEffect(() => {
      const getImagesPreview = async () => {
        const images: Image[] = []
        try {
          const imagesPreview = await client.get(
            `/api/spesa/getimagespreview?expenseId=${props.expense?.id}&path=${props.expense?.attachmentsFolderUrl}`,
            {
              headers: {
                'Content-Type': 'application/json',
              },
            },
          )
          imagesPreview.data.images.map((imagePreview: any) => {
            if (imagePreview.size !== 0) {
              images.push({
                src: `${imagePreview.sasToken}`,
                title: imagePreview.fileName,
                alt: imagePreview.fileName,
                link: `${imagePreview.sasToken}`,
              })
            }
          })
          setImageGallery(images)
        } catch (error: any) {
          if (error.status === 403) {
            setPerm(false)
          }
        } finally {
          setIsLoading(false)
        }
      }

      if (!props.creating && !imageUpload) getImagesPreview()
    }, [imageUpload])

    return (
      <>
        <div id="container-images" style={{ display: 'flex', gap: '5px', flexWrap: 'wrap', paddingBottom: '5px' }}>
          <div
            id="container-image-upload"
            style={{
              textAlign: 'center',
              height: '400px',
              flex: '1',
              minWidth: '300px', // Prevent it from getting too narrow
              borderRadius: '1em',
              border: '0.15em solid #ccc',
              overflow: 'hidden', // Prevent content from spilling out
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <div style={{ flex: '1', overflow: 'auto' }}>
              <FileUploader
                ref={refFileUploader}
                selectButtonText={'Carica immagine'}
                labelText={currentScreenSize.isXSmall ? '' : `o trascina un' immagine qui`}
                readyToUploadMessage=""
                accept="image/*"
                multiple={false}
                uploadMode={'useForm'}
                onValueChanged={onValueChanged}
                showFileList={false}
                style={{ height: '100%' }} // Make it fill available space
              ></FileUploader>
            </div>
            <span>
              <img
                className="thumb"
                src={imageUpload?.src}
                title={imageUpload?.title}
                alt={imageUpload?.title}
                style={{
                  maxHeight: '340px', // Limit image height
                  maxWidth: '100%',
                  objectFit: 'contain',
                }}
              />
            </span>
          </div>
          <div
            id="container-gallery"
            style={{
              height: '400px',
              flex: '1',
              minWidth: '300px', // Prevent it from getting too narrow
              borderRadius: '1em',
              border: '0.15em solid #ccc',
              overflow: 'hidden', // Prevent content from spilling out
            }}
          >
            {isLoading ? (
              <LoadingPanel visible={true} shading={false} position={{ of: `#container-gallery` }}></LoadingPanel>
            ) : (
              <Gallery
                dataSource={imageGallery}
                height="100%"
                id="gallery"
                loop={false}
                showIndicator={true}
                showNavButtons={true}
                slideshowDelay={0}
                noDataText={
                  props.creating
                    ? `Nessun' immagine associata alla spesa`
                    : !perm
                      ? `Non si dispone dei permessi necessari per visualizzare le immagini`
                      : `Nessun' immagine associata alla spesa`
                }
                itemRender={({ link, src, alt, title }) => {
                  return (
                    <div
                      style={{
                        textAlign: 'center',
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <span>
                        <a href={link} target={'_blank'} rel="noreferrer">
                          <img
                            className="thumb"
                            src={src}
                            title={title}
                            alt={alt}
                            style={{
                              maxHeight: '380px', // Leave some space for gallery controls
                              maxWidth: '100%',
                              objectFit: 'contain',
                            }}
                          />
                        </a>
                      </span>
                    </div>
                  )
                }}
              />
            )}
          </div>
        </div>
        {loading && (
          <div id="loadingImageUpload">
            <LoadingPanel visible={loading} shading={false} position={{ of: `#container-image-upload` }}></LoadingPanel>
          </div>
        )}
      </>
    )
  },
)

export default ReceiptOCR
